import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface BannerModel {
    id: number
    urlBanner: string
    exibirCardapio: boolean
    exibirTelaInicial: boolean
}

export interface ObterBannersResposta {
    banners: BannerModel[]
    podeCriarBannerTelaInicial: boolean
}

export interface SalvarBannerPayload {
    urlBanner: string
    exibirCardapio: boolean
    exibirTelaInicial: boolean
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const obterBanners = async function (
    authorization: string
): Promise<ObterBannersResposta | ErroApi> {
    try {
        const response = await api
            .get<RespostaRaw<ObterBannersResposta>>("fornecedor/ObterBanners", {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ObterBannersResposta>(response, "ObterBanners")
    } catch (error) {
        RegistrarLogErro(error, "ObterBanners", "")
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const uploadBanner = async function (
    authorization: string,
    arquivo: File
): Promise<{ url: string } | ErroApi> {
    try {
        const extensao = arquivo.name.split(".").pop() || "jpg"
        const buffer = await arquivo.arrayBuffer()
        const response = await api
            .post<RespostaRaw<{ url: string }>>(
                `fornecedor/UploadBanner?extensao=${extensao}`,
                buffer,
                {
                    headers: {
                        "Content-Type": "application/octet-stream",
                        Authorization: authorization,
                    },
                }
            )
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<{ url: string }>(response, "UploadBanner")
    } catch (error) {
        RegistrarLogErro(error, "UploadBanner", arquivo.name)
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const salvarBanner = async function (
    authorization: string,
    payload: SalvarBannerPayload
): Promise<BannerModel | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<BannerModel>>("fornecedor/SalvarBanner", payload, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<BannerModel>(response, "SalvarBanner")
    } catch (error) {
        RegistrarLogErro(error, "SalvarBanner", JSON.stringify(payload))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

export const excluirBanner = async function (
    authorization: string,
    idBanner: number
): Promise<void | ErroApi> {
    try {
        type RespostaErroRaw = { codErro: number; mensagem?: string }
        const response = await api
            .delete<RespostaErroRaw | null>(`fornecedor/ExcluirBanner/${idBanner}`, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) as RespostaErroRaw }
            })

        const d = response.data
        if (d && typeof d === "object" && "codErro" in d && d.codErro > 0) {
            return { retornoErro: d.codErro, mensagem: d.mensagem ?? "" }
        }
    } catch (error) {
        RegistrarLogErro(error, "ExcluirBanner", String(idBanner))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
